import React from 'react';

import styled from '@emotion/styled';

import { default as CarouselNuka } from 'nuka-carousel';

import { useMediaQuery } from '@zego/hooks/useMediaQuery';
import { unit } from '@zego/theme';

import CarouselTestimonialTile from '@zego/components/molecules/CarouselTestimonialTile';
import ContentContainer from '@zego/components/SCContentContainer';

interface CarouselProps {
  items: CarouselItem[];
}

export interface CarouselItem {
  id: string;
  image: string;
  alt: string;
  text: string;
  customer: string;
}

type CarouselFunctionalityObject = {
  wrapAround?: boolean;
  autoplay?: boolean;
  autoplayInterval?: number;
  edgeEasing?: string;
  slidesToScroll?: number;
  dragging: boolean;
} | null;

export default function CarouselTestimonials({
  items,
}: CarouselProps): JSX.Element {
  const isMobile = useMediaQuery('(max-width: 512px)');
  const isTablet = useMediaQuery('(min-width:513px) and (max-width: 1023px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  function getResponsiveSlidesToShow(): number {
    // Calculate slides to show based on number of `items` and screen sizes
    let slidesToShow: number;
    if (isDesktop) {
      slidesToShow = 3;
    } else {
      isTablet ? (slidesToShow = 2) : (slidesToShow = 1);
    }
    return Math.min(slidesToShow, items.length);
  }

  function getCarouselFunctionality(
    numberOfSlides: number,
  ): CarouselFunctionalityObject {
    // Don't have autoplay or dragging on tablet screens if number of carousels is 2 or less
    const disableInteraction = isTablet && numberOfSlides < 3;
    const smallScreenSettings: CarouselFunctionalityObject = {
      wrapAround: true,
      autoplay: disableInteraction ? false : true,
      autoplayInterval: 5000,
      edgeEasing: 'easePolyOut',
      slidesToScroll: 1,
      dragging: disableInteraction ? false : true,
    };

    if (isTablet || isMobile) {
      return smallScreenSettings;
    }

    const largeScreenSettings: CarouselFunctionalityObject = {
      dragging: false,
    };

    return largeScreenSettings;
  }

  interface ContentContainerStyledProps {
    itemsLength: number;
    responsiveMarginProps: ResponsiveSliderMargins;
  }

  interface ResponsiveSliderMargins {
    mobile: string;
    desktop: string;
    largeDesktop: string;
  }

  function responsiveMarginProps(
    itemsLength: number,
  ): ResponsiveSliderMargins {
    // 3 items in a slide results in less width and increased height, the margin-bottom styles encompass this to prevent content being cut off
    if (itemsLength >= 3) {
      return {
        mobile: `${unit(10)}px !important;`,
        desktop: `${unit(8)}px important;`,
        largeDesktop: `${unit(3)}px important;`,
      };
    } else {
      return {
        mobile: `${unit(6)}px !important;`,
        desktop: `${unit(2)}px !important;`,
        largeDesktop: `${unit(0)}px !important;`,
      };
    }
  }

  const ContentContainerStyled = styled(
    ContentContainer,
  )<ContentContainerStyledProps>`
    padding: 0;
    @media only screen and (min-width: 768px) {
      padding: 0 36px;
    }
    .slider-list {
      min-height: 400px;
      margin-bottom: ${({ itemsLength }): string =>
        responsiveMarginProps(itemsLength).mobile}
      @media only screen and (min-width: 1024px){
        margin-bottom: ${({ itemsLength }): string =>
          responsiveMarginProps(itemsLength).desktop}
      }
      @media only screen and (min-width: 1200px){
        margin-bottom: ${({ itemsLength }): string =>
          responsiveMarginProps(itemsLength).largeDesktop}
      }
    }
    .slider:focus, .slider:active {
      box-shadow: 0;
      outline: 0;
    }
    .slider:focus, .slider:active {
      box-shadow: 0;
      outline: 0;
    }
  `;

  return (
    <ContentContainerStyled
      itemsLength={items.length}
      responsiveMarginProps={responsiveMarginProps(items.length)}
    >
      <CarouselNuka
        slidesToShow={getResponsiveSlidesToShow()}
        initialSlideWidth={200}
        initialSlideHeight={1200}
        heightMode="max"
        withoutControls
        cellAlign="left"
        cellSpacing={16}
        {...getCarouselFunctionality(items.length)}
      >
        {items.map(item => (
          <CarouselTestimonialTile key={item.id} {...item} />
        ))}
      </CarouselNuka>
    </ContentContainerStyled>
  );
}
